import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {Page, Pageable} from '../../../model/rest/base';
import {
  ItemAvailability,
  V3Chapter,
  V3ExamSession,
  V3ExamSessionPart,
  V3ExerciseSession,
  V3ExerciseSessionQuestion,
  V3ExerciseSet,
} from '../../../model/v3/cspa';
import {appendIfDefined, createPageableParams} from './rest-v3-utils';
import {map} from "rxjs/operators";

@Injectable({
  providedIn: 'root',
})
export class CspaRestServiceV3 {
  constructor(private http: HttpClient) {
  }

  private peopleUrl(personId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/people/${personId}${path}`;
  }

  private cspaUrl(path: string): string {
    return `${environment.eventsApiBase}/v1${path}`;
  }

  listPersonAvailabilities(
    personId: number,
    path: string,
    depth: number,
  ): Observable<ItemAvailability[]> {
    let params = new HttpParams();
    params = appendIfDefined(params, 'path', path);
    params = appendIfDefined(params, 'depth', depth);
    return this.http.get<ItemAvailability[]>(
      this.peopleUrl(personId, '/exercise-progress-tree'),
      {params},
    );
  }

  findExamSessions(
    personId: number,
    pathPrefix: string,
  ): Observable<V3ExamSession[]> {
    // schoolId kept for compatibility with legacy callers.
    let params = new HttpParams();
    params = appendIfDefined(params, 'pathPrefix', pathPrefix || null);
    return this.http.get<V3ExamSession[]>(
      this.peopleUrl(personId, '/exam-sessions'),
      {params},
    );
  }

  findExamSessionsParts(
    personId: number,
    uuid: string,
  ): Observable<V3ExamSessionPart[]> {
    return this.http.get<V3ExamSessionPart[]>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts`)
    );
  }

  getPartQuestions(
    personId: number,
    uuid: string,
    partNum: number,
  ): Observable<V3ExerciseSessionQuestion[]> {
    return this.http.get<V3ExerciseSessionQuestion[]>(
      this.peopleUrl(personId, `/exam-sessions/${uuid}/parts/${partNum}/questions`)
    );
  }

  listExerciseSets(): Observable<V3ExerciseSet[]> {
    return this.http.get<V3ExerciseSet[]>(this.cspaUrl('/struct/sets'));
  }


  listChapters(path: string): Observable<V3Chapter[]> {
    return this.getStructureBranch(path, 3).pipe(
      map(chapters => chapters as V3Chapter[]),
    )
  }

  findSessions(
    personId: number,
    pathPrefix: string,
    pageable?: Pageable,
  ): Observable<Page<V3ExerciseSession>> {
    let params = createPageableParams(pageable);
    params = appendIfDefined(params, 'pathPrefix', pathPrefix || null);
    return this.http.get<Page<V3ExerciseSession>>(
      this.peopleUrl(personId, '/exercise-sessions'),
      {params},
    );
  }

  getStructureBranch(pathExpression: string, depth?: number): Observable<unknown[]> {
    let params = new HttpParams();
    params = appendIfDefined(params, 'depth', depth ?? null);
    return this.http.get<unknown[]>(
      this.cspaUrl(`/struct/${encodeURIComponent(pathExpression)}`),
      {params},
    );
  }
}
